import { useQuery } from "@tanstack/react-query";
import { useDynamicSupabase } from "@/hooks/useDynamicSupabase";

export interface PackageKPI {
  packageId?: string;
  conversionRate: number;
  averageBookingValue: number;
  customerSatisfaction: number;
  cancellationRate: number;
  repeatBookingRate: number;
  totalViews: number;
  totalInquiries: number;
  totalBookings: number;
}

export const usePackageKPIs = (packageId?: string) => {
  const { supabase } = useDynamicSupabase();
  
  return useQuery({
    queryKey: ["package-kpis", packageId],
    queryFn: async (): Promise<PackageKPI> => {
      let kpiQuery = supabase
        .from("package_kpi_metrics")
        .select("*") as any;

      if (packageId) {
        kpiQuery = kpiQuery.eq('package_id', packageId);
      }

      const { data: kpis, error: kpisError } = await kpiQuery;

      if (kpisError) throw kpisError;

      const totalViews = kpis?.reduce((sum: number, k: any) => sum + (k.views_count || 0), 0) || 0;
      const totalInquiries = kpis?.reduce((sum: number, k: any) => sum + (k.inquiry_count || 0), 0) || 0;
      const totalBookings = kpis?.reduce((sum: number, k: any) => sum + (k.booking_count || 0), 0) || 0;
      
      const avgBookingValue = kpis?.reduce((sum: number, k: any) => sum + (k.average_booking_value || 0), 0) / (kpis?.length || 1) || 0;
      const avgSatisfaction = kpis?.reduce((sum: number, k: any) => sum + (k.customer_satisfaction_score || 0), 0) / (kpis?.length || 1) || 0;
      const avgCancellation = kpis?.reduce((sum: number, k: any) => sum + (k.cancellation_rate || 0), 0) / (kpis?.length || 1) || 0;
      const avgRepeat = kpis?.reduce((sum: number, k: any) => sum + (k.repeat_customer_rate || 0), 0) / (kpis?.length || 1) || 0;
      const avgConversion = kpis?.reduce((sum: number, k: any) => sum + (k.booking_conversion_rate || 0), 0) / (kpis?.length || 1) || 0;

      return {
        packageId,
        conversionRate: Number(avgConversion.toFixed(2)),
        averageBookingValue: Number(avgBookingValue.toFixed(2)),
        customerSatisfaction: Number(avgSatisfaction.toFixed(2)),
        cancellationRate: Number(avgCancellation.toFixed(2)),
        repeatBookingRate: Number(avgRepeat.toFixed(2)),
        totalViews,
        totalInquiries,
        totalBookings
      };
    }
  });
};
